import type { Nullable } from "@piperunner-cms/types/v1";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// takes a schema and returns a key value pair of default values
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function extractDefaultValues(schema: Record<string, Partial<{ defaultValue: any }>>) {
  return Object.fromEntries(Object.entries(schema).map(([key, { defaultValue }]) => [key, defaultValue]));
}

interface CommonFieldData {
  label: string;
  subLabel?: string;
  helpText?: string;
  description?: string;
  placeholder: string;
  validationType: z.ZodTypeAny;
  className?: string;
}

interface TextFieldData extends CommonFieldData {
  defaultValue: string;
  type: "text" | "date" | "select" | "textarea" | "richtext";
}

interface MultiSelect extends CommonFieldData {
  defaultValue: string[];
  type: "multiselect";
}

interface ImageFieldData extends CommonFieldData {
  defaultValue: string;
  aspectRatio?: number;
  maxSize?: number; // in bytes, default is 1MB
  type: "image";
  compact?: boolean; // if true, will use compact styles
  alt?: string;
}

interface ArrayFieldData extends CommonFieldData {
  defaultValue: Nullable<[]>;
  type: "array";
}

interface CheckboxFieldData extends CommonFieldData {
  defaultValue: boolean;
  type: "checkbox";
}

interface ColorFieldData extends CommonFieldData {
  defaultValue: string;
  type: "color";
}

export type FormFieldData =
  | TextFieldData
  | ImageFieldData
  | CheckboxFieldData
  | ArrayFieldData
  | ColorFieldData
  | MultiSelect;

export function generateValidationObjectMap(formFieldMap: Record<string, FormFieldData>): Record<string, z.ZodTypeAny> {
  return Object.fromEntries(
    Object.entries(formFieldMap)
      .filter(([_, value]) => value)
      .map(([key, value]) => [key, value.validationType]),
  );
}
